/*  ========================================================================
    # Section - Timeline - Entry
    ========================================================================  */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';

// Components
import ImageSlider from '../../components/ImageSlider/ImageSlider';

const TimelineEntry = ({ entryIndex, entry, setLightboxImage, setLightboxIsOpen }) => {
  const [entryYCoordinate, setEntryYCoordinate] = useState(0);
  const [entryPosition, setEntryPosition] = useState(1);

  /**
   * Check if the index is odd or even.
   *
   * @param {number} index
   * @return {string}
   */
  const checkOddOrEven = index => {
    let result;

    if (index % 2 !== 0) result = 'odd';
    if (index % 2 === 0) result = 'even';

    return result;
  };

  /**
   * Set the corresponding CSS classes.
   *
   * @param {number} index
   * @param {string} colType
   * @return {string}
   */
  const setColClasses = (index, colType) => {
    const defaultClasses =
      'col tw-w-full md:tw-w-10/12 md:tw-ml-[calc(2/12*100%)] lg:tw-w-5/12 xxl:tw-w-4/12';
    let classes = '';

    // Content
    if (colType === 'content') {
      if (checkOddOrEven(index) === 'odd') {
        classes = `s-timeline__content ${defaultClasses} lg:tw-ml-[calc(2/12*100%)]`;
      }
      if (checkOddOrEven(index) === 'even') {
        classes = `s-timeline__content ${defaultClasses} lg:tw-ml-0 xxl:tw-ml-[calc(1/12*100%)] lg:tw-text-right`;
      }
    }

    // Slider
    if (colType === 'slider') {
      if (checkOddOrEven(index) === 'odd') {
        classes = `${defaultClasses} lg:tw-ml-0 xxl:tw-ml-[calc(1/12*100%)]`;
      }
      if (checkOddOrEven(index) === 'even') {
        classes = `${defaultClasses} lg:tw-ml-[calc(2/12*100%)]`;
      }
    }

    return classes;
  };

  /**
   * Intersection Observer for entry.
   */
  const {
    ref: entryRef,
    inView: entryInView,
    entry: entryIO
  } = useInView({
    threshold: 0.4
  });

  /**
   * Set entry position.
   */
  useEffect(() => {
    setEntryYCoordinate(prev => {
      if (entryInView) {
        setEntryPosition(0);
      } else if (prev > entryIO?.boundingClientRect.y) {
        setEntryPosition(-1); // Scoll down
      } else {
        setEntryPosition(1); // Scroll up
      }

      return entryIO?.boundingClientRect.y;
    });
  }, [entryIO?.boundingClientRect.y]);

  /**
   * Intersection Observer for span.
   */
  const { ref: spanRef, inView: spanInView } = useInView({
    rootMargin: '0% 0% -50% 0%',
    threshold: 0.5
  });

  /**
   *
   * @param {Object} image
   */
  const clickedImageHandler = image => {
    setLightboxImage(image);
    setLightboxIsOpen(true);
  };

  return (
    <div
      ref={entryRef}
      className={`s-timeline__entry row ${checkOddOrEven(entryIndex) === 'odd' ? 'lg:tw-flex-row-reverse lg:tw-justify-end' : ''
        }`}
      data-position={entryPosition}
    >
      {/* Content */}
      <div className={setColClasses(entryIndex, 'content')}>
        <span
          ref={spanRef}
          className={`${checkOddOrEven(entryIndex) === 'odd' ? 'odd' : 'even'} ${spanInView ? 'active' : ''
            }`}
        >
          {entry.year}
        </span>
        <h2 className='h4'>{entry.title}</h2>
        <div className='wysiwyg-block' dangerouslySetInnerHTML={{ __html: entry.text }} />
      </div>
      {/* Slider */}
      <ImageSlider
        classes={setColClasses(entryIndex, 'slider')}
        entries={entry.images}
        onClickImage={clickedImageHandler}
      />
    </div>
  );
};

TimelineEntry.propTypes = {
  entryIndex: PropTypes.number,
  entry: PropTypes.object,
  setLightboxImage: PropTypes.func,
  setLightboxIsOpen: PropTypes.func
};

export default TimelineEntry;
